import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Formats a date to YYYY-MM-DD format (ISO date format)
 * Uses dayjs for robust date handling and timezone-safe parsing
 * 
 * @param {Date|string} date - Date to format (can be Date object or string)
 * @returns {string|null} Formatted date string in YYYY-MM-DD format, or null if invalid
 */
export default function formatDateToYYYYMMDD(date) {
  if (!date) return null;
  
  // If it's already a string in YYYY-MM-DD format, return it directly
  // This avoids any parsing and timezone issues completely
  if (typeof date === 'string') {
    const yyyyMMddRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (yyyyMMddRegex.test(date)) {
      return date;
    }
    
    // Try to parse with dayjs using explicit format to avoid timezone issues
    // Using customParseFormat to parse as local date (not UTC)
    const parsed = dayjs(date, 'YYYY-MM-DD', true); // strict mode
    if (!parsed.isValid()) {
      // Fallback: try general parsing
      const fallbackParsed = dayjs(date);
      if (!fallbackParsed.isValid()) return null;
      return fallbackParsed.format('YYYY-MM-DD');
    }
    return parsed.format('YYYY-MM-DD');
  }
  
  // If it's a Date object, use dayjs to format it
  // dayjs formats Date objects using local timezone, which is what we want
  if (date instanceof Date) {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return null;
    return parsed.format('YYYY-MM-DD');
  }
  
  return null;
}

